import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { useGetPasskeyRegistrationOptions } from "./useGetPasskeyRegistrationOptions";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useRegisterPasskey } from "./useRegisterPasskey";
import { useGetPasskeyAuthenticationOptions } from "./useGetPasskeyAuthenticationOptions";
import { useAuthenticatePasskey } from "./useAuthenticatePasskey";
import { IToken } from "../users";
import { strings } from "../../localization/LocalizedStrings";

export { useGetPasskeyRegistrationOptions } from "./useGetPasskeyRegistrationOptions";
export { useRegisterPasskey } from "./useRegisterPasskey";

export const usePasskeys = () => {
  const getPasskeyRegistrationOptions = useGetPasskeyRegistrationOptions();
  const getPasskeyAuthenticationOptions = useGetPasskeyAuthenticationOptions();
  const verifyRegistration = useRegisterPasskey();
  const verifyAuthentication = useAuthenticatePasskey();

  const registerPasskey = async (onSuccess: () => void, onError: (message: string) => void) => {
    const { status, body } = await getPasskeyRegistrationOptions();
    if (StatusCodesHelper.isSuccessful(status)) {
      try {
        const credentialResponse = await startRegistration({ optionsJSON: body });
        await performRegistration(credentialResponse, onSuccess, onError);
      } catch (error: any) {
        if (error.name && ["NotAllowedError", "AbortError"].includes(error.name)) {
          // Ignore, since user canceled the request
          return;
        }
        onError(strings.passkey_registration_error);
      }
    } else {
      onError(body.message);
    }
  };

  const performRegistration = async (
    credentialResponse: any,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    const { status, body } = await verifyRegistration(credentialResponse);
    if (StatusCodesHelper.isSuccessful(status)) {
      onSuccess();
    } else {
      onError(body.message);
    }
  };

  const authenticatePasskey = async (
    setLoading: (isLoading: boolean) => void,
    onError: (message: string) => void,
    callback: (token: IToken) => void,
    useBrowserAutofill: boolean
  ) => {
    const { status, body } = await getPasskeyAuthenticationOptions();
    if (StatusCodesHelper.isSuccessful(status)) {
      try {
        const authenticationResponse = await startAuthentication({
          optionsJSON: body,
          useBrowserAutofill: useBrowserAutofill
        });
        await performAuthentication(
          body.challenge,
          authenticationResponse,
          setLoading,
          onError,
          callback
        );
      } catch (error: any) {
        if (error.name && ["NotAllowedError", "AbortError"].includes(error.name)) {
          // Ignore, since user canceled the request
          return;
        }
        onError(strings.passkey_authentication_error);
      }
    }
  };

  const performAuthentication = async (
    challenge: string,
    authenticationResponse: any,
    setLoading: (isLoading: boolean) => void,
    onError: (message: string) => void,
    callback: (token: IToken) => void
  ) => {
    setLoading(true);
    const { status, body } = await verifyAuthentication(challenge, authenticationResponse);
    if (StatusCodesHelper.isSuccessful(status)) {
      callback(body);
    } else {
      onError(body.message);
    }
    setLoading(false);
  };

  return { registerPasskey, authenticatePasskey };
};
